import { AlertTriangle } from 'lucide-react';

interface Props {
  stock?: number | null;
  className?: string;
}

/**
 * Small urgency badge shown above the Add-to-Cart button.
 * - stock 1-5  → "Only X left in stock"  (red)
 * - stock 6-10 → "Low stock"              (amber)
 * - else       → renders nothing
 *
 * Backend supplies stock as Product.stock (number | null).
 * Status colors are semantic and intentionally hardcoded — they should
 * read the same across every template theme (success/warning/danger
 * convention). Body color is dark on a tinted bg, WCAG AA compliant.
 */
export function StockUrgency({ stock, className = '' }: Props) {
  if (typeof stock !== 'number' || stock <= 0 || stock > 10) return null;

  const isCritical = stock <= 5;
  const text = isCritical ? `Only ${stock} left in stock` : `Low stock — ${stock} left`;
  const palette = isCritical
    ? { color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' }
    : { color: '#92400e', bg: '#fef3c7', border: '#fcd34d' };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${className}`}
      style={{
        color: palette.color,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
      {text}
    </div>
  );
}
